import classNames from 'classnames';
import { useState } from 'react';
import type { ReactNode } from 'react';

import { useUiMessages } from '../../messages';
import { Button } from '../Button';
import { Calendar } from '../Calendar';
import type { CalendarSelection, DateRange } from '../Calendar';
import { DEFAULT_MONTH_LABELS, startOfDay, startOfMonth } from '../Calendar/dates';
import { Typography } from '../Typography';

import styles from './DatePicker.module.css';

export type { CalendarSelection, DateRange } from '../Calendar';

export type DatePickerMode = 'single' | 'range';

export interface DatePickerPreset {
  label: string;
  /** Value applied when the preset is picked (a `Date` in single mode, a `DateRange` in range). */
  value: Date | DateRange;
}

export interface DatePickerLabels {
  today: string;
  cancel: string;
  apply: string;
  startPlaceholder: string;
  endPlaceholder: string;
}

const DEFAULT_LABELS: DatePickerLabels = {
  today: 'Today',
  cancel: 'Cancel',
  apply: 'Apply',
  startPlaceholder: 'Start date',
  endPlaceholder: 'End date',
};

const SHORT_MONTH_LEN = 3;

export interface DatePickerProps {
  mode?: DatePickerMode;
  value?: CalendarSelection;
  defaultValue?: CalendarSelection;
  /** Fires on every draft change inside the menu (before Apply). */
  onChange?: (value: CalendarSelection) => void;
  /** Fires with the committed value when Apply is pressed. */
  onApply?: (value: CalendarSelection) => void;
  onCancel?: () => void;
  numberOfMonths?: number;
  /** Quick-select shortcuts shown as a sidebar (range) / above the grid. */
  presets?: DatePickerPreset[];
  weekStartsOn?: 0 | 1;
  minDate?: Date;
  maxDate?: Date;
  isDateDisabled?: (date: Date) => boolean;
  hasDot?: (date: Date) => boolean;
  monthLabels?: string[];
  weekdayLabels?: string[];
  weekdayLabelsFull?: string[];
  labels?: Partial<DatePickerLabels>;
  /** Format a date for the input display (defaults to `Mon D, YYYY`). */
  formatDate?: (date: Date) => string;
  className?: string;
}

function firstOf(value: CalendarSelection): Date | null {
  if (value == null) return null;
  if (value instanceof Date) return value;
  return value.from;
}

function asRange(value: CalendarSelection): DateRange {
  if (value != null && !(value instanceof Date)) return value;
  return { from: null, to: null };
}

/**
 * Date picker menu: the `Calendar` grid wrapped in the Figma card — month nav, a read-only
 * value field with a Today shortcut, optional quick-select presets and a Cancel / Apply
 * footer. Edits happen on an internal draft; `onApply` commits it, `onCancel` reverts.
 * Single-date or range (with a preset sidebar and two months). All labels arrive as props.
 *
 * @interim Seeded from the Figma "Date picker menu"; replaceable by the UI Kit without API breakage.
 */
export function DatePicker({
  mode = 'single',
  value,
  defaultValue,
  onChange,
  onApply,
  onCancel,
  numberOfMonths = 1,
  presets,
  weekStartsOn = 1,
  minDate,
  maxDate,
  isDateDisabled,
  hasDot,
  monthLabels,
  weekdayLabels,
  weekdayLabelsFull,
  labels,
  formatDate,
  className,
}: DatePickerProps) {
  const messages = useUiMessages();
  const t = { ...DEFAULT_LABELS, ...messages.datePicker, ...labels };
  const resolvedMonthLabels = monthLabels ?? messages.calendar?.months ?? DEFAULT_MONTH_LABELS;
  const resolvedWeekdayLabels = weekdayLabels ?? messages.calendar?.weekdaysShort;
  const resolvedWeekdayLabelsFull = weekdayLabelsFull ?? messages.calendar?.weekdaysFull;
  const isRangeMode = mode === 'range';
  /* Two-month range → vertical preset sidebar + fields in the footer (Figma "Date range menu").
     One-month range → fields and a horizontal preset row sit above the grid, footer is actions only. */
  const useSidebar = isRangeMode && numberOfMonths > 1;
  const initial: CalendarSelection =
    value ?? defaultValue ?? (isRangeMode ? { from: null, to: null } : null);

  const [draft, setDraft] = useState<CalendarSelection>(initial);
  const [month, setMonth] = useState(() =>
    startOfMonth(firstOf(initial) ?? startOfDay(new Date())),
  );

  const fmt =
    formatDate ??
    ((d: Date) =>
      `${resolvedMonthLabels[d.getMonth()]?.slice(0, SHORT_MONTH_LEN) ?? ''} ${String(d.getDate())}, ${String(d.getFullYear())}`);

  const selectDraft = (next: CalendarSelection) => {
    setDraft(next);
    const anchor = firstOf(next);
    if (anchor != null) setMonth(startOfMonth(anchor));
    onChange?.(next);
  };

  const goToday = () => {
    const today = startOfDay(new Date());
    const next: CalendarSelection = isRangeMode ? { from: today, to: null } : today;
    setDraft(next);
    setMonth(startOfMonth(today));
    onChange?.(next);
  };

  const applyPreset = (preset: DatePickerPreset) => {
    setDraft(preset.value);
    const anchor = firstOf(preset.value);
    if (anchor != null) setMonth(startOfMonth(anchor));
    onChange?.(preset.value);
  };

  const cancel = () => {
    setDraft(initial);
    setMonth(startOfMonth(firstOf(initial) ?? startOfDay(new Date())));
    onCancel?.();
  };

  const draftRange = asRange(draft);
  const draftDate = draft instanceof Date ? draft : null;
  const activePreset = (preset: DatePickerPreset) => {
    const a = firstOf(preset.value);
    const b = firstOf(draft);
    if (a == null || b == null) return false;
    return a.getTime() === b.getTime() && sameEnd(preset.value, draft);
  };

  const field = (date: Date | null, placeholder: string) => (
    <div className={styles.field}>
      <Typography
        as="span"
        variant="text-md"
        className={classNames(date != null ? styles.fieldValue : styles.fieldPlaceholder)}
      >
        {date != null ? fmt(date) : placeholder}
      </Typography>
    </div>
  );

  const inputRow: ReactNode = isRangeMode ? (
    <div className={styles.rangeFields}>
      {field(draftRange.from, t.startPlaceholder)}
      <span className={styles.dash} aria-hidden="true">
        –
      </span>
      {field(draftRange.to, t.endPlaceholder)}
    </div>
  ) : (
    <div className={styles.singleRow}>
      {field(draftDate, t.startPlaceholder)}
      <Button hierarchy="secondary" size="md" onClick={goToday}>
        {t.today}
      </Button>
    </div>
  );

  const presetItems =
    presets != null && presets.length > 0
      ? presets.map((preset) => (
          <li key={preset.label}>
            <button
              type="button"
              className={classNames(styles.preset, activePreset(preset) && styles.presetActive)}
              aria-pressed={activePreset(preset)}
              onClick={() => {
                applyPreset(preset);
              }}
            >
              <Typography as="span" variant="text-sm" weight="medium">
                {preset.label}
              </Typography>
            </button>
          </li>
        ))
      : null;

  /* Above-grid content: single mode → the field + Today shortcut; inline range → the from–to
     fields with a horizontal preset row. Sidebar range keeps the grid header clean. */
  const belowHeader: ReactNode = useSidebar ? null : isRangeMode ? (
    <div className={styles.inlineHead}>
      {inputRow}
      {presetItems != null && <ul className={styles.presetRow}>{presetItems}</ul>}
    </div>
  ) : (
    inputRow
  );

  const calendar = (
    <Calendar
      mode={mode}
      selected={draft}
      onSelect={selectDraft}
      month={month}
      onMonthChange={setMonth}
      numberOfMonths={numberOfMonths}
      weekStartsOn={weekStartsOn}
      {...(minDate != null ? { minDate } : {})}
      {...(maxDate != null ? { maxDate } : {})}
      {...(isDateDisabled != null ? { isDateDisabled } : {})}
      {...(hasDot != null ? { hasDot } : {})}
      monthLabels={resolvedMonthLabels}
      {...(resolvedWeekdayLabels != null ? { weekdayLabels: resolvedWeekdayLabels } : {})}
      {...(resolvedWeekdayLabelsFull != null
        ? { weekdayLabelsFull: resolvedWeekdayLabelsFull }
        : {})}
      {...(belowHeader != null ? { belowHeaderSlot: belowHeader } : {})}
    />
  );

  return (
    <div
      className={classNames(styles.card, className)}
      data-layout={useSidebar ? 'sidebar' : 'inline'}
    >
      <div className={styles.top}>
        {useSidebar && presetItems != null && <ul className={styles.presets}>{presetItems}</ul>}
        <div className={styles.body}>{calendar}</div>
      </div>
      <div className={styles.footer}>
        {useSidebar && <div className={styles.footerFields}>{inputRow}</div>}
        <div className={styles.footerActions}>
          <Button hierarchy="secondary" size="md" className={styles.action} onClick={cancel}>
            {t.cancel}
          </Button>
          <Button
            hierarchy="primary"
            size="md"
            className={styles.action}
            onClick={() => {
              onApply?.(draft);
            }}
          >
            {t.apply}
          </Button>
        </div>
      </div>
    </div>
  );
}

function sameEnd(a: Date | DateRange, b: CalendarSelection): boolean {
  const aEnd = a instanceof Date ? a : a.to;
  const bEnd = b instanceof Date ? b : (b?.to ?? null);
  if (aEnd == null && bEnd == null) return true;
  if (aEnd == null || bEnd == null) return false;
  return aEnd.getTime() === bEnd.getTime();
}
